import React, { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";

import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";

import moment from "moment";

import { Button, useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";

import {
  FIRE_EMERGENCY_SR_MANAGER,
  FIRE_SAFETY_CONSUMER,
} from "common/constant";
import { getOptionLabel } from "common/enums";
import { MOCK_DRILL_DETAILS } from "tagTypes";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import FireSafetyMockDrillDetailFilter from "./FireSafetyMockDrillDetailFilter";
import FireSafetyMockDrillActions from "./FireSafetyMockDrillActions";
import MockDrillScheduleModal from "./MockDrillScheduleModal";
import MockDrillViewModal from "./MockDrillViewModal";

const RECORDS_PER_PAGE = 10;

const MockDrillDetails = ({ t }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { roles } = useSelector((state) => state.Login);

  const moackDrillDetailId = +searchParams.get("mockDrillDetailId");

  const [query, setQuery] = useState({});
  const lg = useMediaQuery("(min-width: 768px) and (max-width: 1399px)");
  const md = useMediaQuery("(min-width: 576px) and (max-width: 767px)");
  const sm = useMediaQuery("(max-width: 575px)");

  const [isOpen, setIsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const resetPageRef = useRef();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const isAdmin = useMemo(() => !roles.includes(FIRE_SAFETY_CONSUMER), [roles]);
  const isSrManager = useMemo(
    () => roles.includes(FIRE_EMERGENCY_SR_MANAGER),
    [roles]
  );

  const viewMockDrillDetailhandler = (id) => {
    searchParams.set("mockDrillDetailId", id);
    setSearchParams(searchParams);
  };

  const columns = [
    {
      title: "Building Name",
      field: "developerBuilding.name",
    },
    {
      title: "Mock Drill Conducted On",
      renderCell: ({ mockDrillConductedOn }) =>
        moment(mockDrillConductedOn).isValid()
          ? convertUTCDateToLocalDate(mockDrillConductedOn).format("DD-MM-YYYY")
          : "N/A",
    },
    {
      title: "Next Mock Drill Due On",
      renderCell: ({ nextMockDrillDueOn }) =>
        moment(nextMockDrillDueOn).isValid()
          ? convertUTCDateToLocalDate(nextMockDrillDueOn).format("DD-MM-YYYY")
          : "N/A",
    },
    {
      title: "Status",
      renderCell: ({ status }) => getOptionLabel("mockDrillStatusEnum", status),
    },
    {
      title: "Actions",
      renderCell: (item) => (
        <FireSafetyMockDrillActions
          item={item}
          isAdmin={isAdmin}
          isSrManager={isSrManager}
          onEdit={(id) => {
            setSelectedId(id);
            setIsOpen(true);
          }}
          viewMockDrillDetailhandler={viewMockDrillDetailhandler}
        />
      ),
    },
  ];

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/mock-drill-schedule",
      tags: [MOCK_DRILL_DETAILS],
      query,
    }),
    [query]
  );

  return (
    <div className="page-content h-100">
      {!!moackDrillDetailId && (
        <MockDrillViewModal
          moackDrillDetailId={moackDrillDetailId}
          handleClose={() => {
            searchParams.delete("mockDrillDetailId");
            setSearchParams(searchParams);
          }}
        />
      )}
      <BreadCrumb title={t("Mock Drill Details")} />

      {isOpen && (
        <MockDrillScheduleModal
          isOpen={isOpen}
          id={selectedId}
          handleClose={() => {
            setIsOpen(false);
            setSelectedId(null);
          }}
        />
      )}

      <FireSafetyMockDrillDetailFilter
        setQuery={setQuery}
        resetPage={handleResetPage}
      />

      {isSrManager && (
        <div className="text-end mb-2">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsOpen(true)}
          >
            {t("Add Mock Drill Schedule")}
          </Button>
        </div>
      )}

      <BaseTable
        columns={columns}
        pageSize={RECORDS_PER_PAGE}
        paginationType="serverSide"
        serverSidePaginationConfig={serverSidePaginationConfig}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        maxHeight={
          sm
            ? "calc(100vh - 400px)"
            : md
            ? "calc(100vh - 360px)"
            : lg
            ? "calc(100vh - 120px)"
            : "calc(100vh - 170px)"
        }
      />
    </div>
  );
};

MockDrillDetails.prototype = {
  t: PropTypes.func,
};

export default withTranslation()(MockDrillDetails);
